import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorMappingService } from '../services/error-mapping.service';
import { UserService } from '../services/user.service';
import { WHITELIST_RCH_API } from '../services/rch.service';

/**
 * It catches some errors on HTTP responses.
 * Based on the intercepted error it decides whether to log off the user
 * and / or show a standard error message or not (whitelisted).
 */
@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {
  private errors = inject(ErrorMappingService);
  private userService = inject(UserService);

  // The interceptor is a singleton and `catchError` runs long after `intercept` returned, so the
  // request being classified is closed over rather than stashed on the instance: with the detail
  // page polling every 15s, an instance field would routinely describe some *other* in-flight
  // request by the time its error arrived.
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (this.isUnauthorized(err)) {
            this.userService.logout();
          }
          if (!this.isWhiteListed(err, request)) {
            this.errors.mapError(err);
          }
          if (!this.isErrorWhiteListed(request.url)) {
            console.error(err);
          }
        }
        throw err;
      })
    );
  }

  private whiteList: Array<string | RegExp> = [
    '/contact-center',
    ...WHITELIST_RCH_API,
    '/disabilities',
    '/webhooks',
    '/shifts',
    RegExp('v5/requests/.+/fleet'),
    '/drivers/license',
    RegExp('v5/requests/.+/driver'),
    // The Tariffario tab words every failure of its endpoints itself, reads included: a stale save is
    // a banner, a refused tariff is marked on its fields, and a 403 or 404 is the tab's own state.
    // v6 only: /v4/rate-schedules belongs to the NCC screen, which relies on the bottom sheet.
    RegExp('/v6/rate-schedules(/|$)'),
    RegExp('/v6/fleets/[^/]+/rate-schedules$'),
  ];

  /**
   * Whitelist entries that hold for one method only.
   *
   * `whiteList` matches on URL alone, which is too blunt for `/v4/requests/:id`: the GET is the
   * detail read, and a 404 there — an unknown id, or one this account's fleets do not reach —
   * is rendered by the detail page itself, so a modal would say the same thing twice. The PATCH
   * on that same URL is a booking edit, whose failure the operator does have to be told about.
   */
  private methodWhiteList: Array<{ method: string; url: RegExp }> = [
    { method: 'GET', url: RegExp('/v[0-9]+/requests/[0-9a-fA-F]{24}$') },
    // The DRT configuration writes report their own failures, in Italian, keyed off the error code
    // the endpoint sends. The bottom sheet would say the same thing beside the toastr, in English.
    //
    // By method, not by URL: `/v6/drt/drivers/search` and `/physical-vehicles/search` are the
    // service-day editor's pickers, and those reads have no toastr — a URL entry would make a
    // failed picker fail silently.
    //
    // Temporary. 2026-08-30 §6 inverts this interceptor's default and deletes both whitelists;
    // these three go with them.
    { method: 'POST', url: RegExp('/v[0-9]+/drt/(service-days|drivers|physical-vehicles)$') },
    { method: 'PUT', url: RegExp('/v[0-9]+/drt/(service-days|drivers|physical-vehicles)/[^/]+$') },
    { method: 'DELETE', url: RegExp('/v[0-9]+/drt/(service-days|drivers|physical-vehicles)/[^/]+$') },
    // The mobile whitelist screen toasts its create and delete failures itself, localized off the
    // error code (a number already registered, …). Its list read renders its own error state but
    // stays reported, like any read.
    { method: 'POST', url: RegExp('/v[0-9]+/admin/whitelist/(user|driver)/mobile$') },
    { method: 'DELETE', url: RegExp('/v[0-9]+/admin/whitelist/(user|driver)/mobile$') },
  ];

  private errorWhiteList: string[] = ['/assets', '/disabilities', '/driver'];

  isErrorWhiteListed(url: string): boolean {
    return this.errorWhiteList.some((errorWhiteList) => url.includes(errorWhiteList));
  }

  whitelistItemIncludedInUrl(url: string, method: string): boolean {
    const urlMatch = this.whiteList.some((whiteListItem: string | RegExp) => {
      if (whiteListItem instanceof RegExp) {
        return whiteListItem.test(url);
      }
      return url.includes(whiteListItem);
    });
    return urlMatch || this.methodWhiteList.some((item) => item.method === method && item.url.test(url));
  }

  /**
   * Determines if an HTTP error is whitelisted. Change if the rules for determining whether an error is whitelisted change.
   * @param {HttpErrorResponse} err
   * @returns {boolean} isWhiteListed
   */
  isWhiteListed(err: HttpErrorResponse, request: HttpRequest<any>): boolean {
    switch (err.status) {
      case 429:
        return true; // dont show popup when "Error 429: Too many requests"
      default:
        return this.whitelistItemIncludedInUrl(request.url, request.method);
    }
  }
  /**
   * Determines if an HTTP error is unauthorized. Change if the rules for determining whether an error is unauthorized change.
   * @param {HttpErrorResponse} err
   * @returns {boolean} isUnauthorized
   */
  isUnauthorized(err: HttpErrorResponse): boolean {
    switch (err.status) {
      case 401:
        return true;
      default:
        return false;
    }
  }
}
